import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';
import { AppContext, AuthContext } from 'Context';
import { CloudServiceConnector } from './components/CloudServiceConnector';
import { FileListingArea } from './components/FileListingArea';
import { AffiliateMemberDropdown } from './components/AffiliateMemberDropdown';
import { DropboxFileBrowser } from './components/DropboxFileBrowser';
import { GoogleDriveFileBrowser } from './components/GoogleDriveFileBrowser';
import { handleDropboxOAuthCallback } from './utils/dropboxOAuthHandler';
import { handleGoogleDriveOAuthCallback } from './utils/googleDriveOAuthHandler';
import styles from './CloudUpload.module.css';

export const CloudUpload = () => {
  const navigate = useNavigate();
  const { getToken } = useContext(AuthContext);
  const { setAppSnackbar } = useContext(AppContext);

  // Initialize state from localStorage
  const [connectedServices, setConnectedServices] = useState(() => {
    const saved = localStorage.getItem('cloudConnectedServices');
    return saved ? JSON.parse(saved) : { dropbox: null, googledrive: null };
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDropboxBrowser, setShowDropboxBrowser] = useState(false);
  const [showGoogleDriveBrowser, setShowGoogleDriveBrowser] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const handleServiceConnect = (service, connectionData) => {
    const newServices = {
      ...connectedServices,
      [service]: connectionData,
    };
    setConnectedServices(newServices);
    // Persist to localStorage
    localStorage.setItem('cloudConnectedServices', JSON.stringify(newServices));
    setAppSnackbar({
      isVisible: true,
      message: `Successfully connected to ${service}`,
      type: 'success',
    });
  };

  const handleServiceDisconnect = (service) => {
    try {
      if (!service) return;

      const newServices = {
        ...connectedServices,
        [service]: null,
      };
      setConnectedServices(newServices);

      // Persist to localStorage
      try {
        localStorage.setItem('cloudConnectedServices', JSON.stringify(newServices));
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to save to localStorage:', e);
      }

      // Also remove files from disconnected service
      setSelectedFiles((prev) => prev.filter((file) => file.source !== service));

      // Format service name properly
      const serviceName = service === 'googledrive' ? 'Google Drive' : service.charAt(0).toUpperCase() + service.slice(1);

      // Use setTimeout to avoid potential race conditions with state updates
      setTimeout(() => {
        if (setAppSnackbar && typeof setAppSnackbar === 'function') {
          setAppSnackbar({
            isVisible: true,
            message: `Disconnected from ${serviceName}`,
            type: 'success',
          });
        }
      }, 0);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error disconnecting service:', error);
      setTimeout(() => {
        if (setAppSnackbar && typeof setAppSnackbar === 'function') {
          setAppSnackbar({
            isVisible: true,
            message: 'Failed to disconnect service',
            type: 'error',
          });
        }
      }, 0);
    }
  };

  const handleImportFiles = (service) => {
    if (service === 'dropbox' && connectedServices.dropbox) {
      setShowDropboxBrowser(true);
    } else if (service === 'googledrive' && connectedServices.googledrive) {
      setShowGoogleDriveBrowser(true);
    }
  };

  const handleFilesSelected = (files, source) => {
    const filesWithSource = files.map((file) => ({
      ...file,
      source,
    }));
    setSelectedFiles((prev) => [...prev, ...filesWithSource]);
    setShowDropboxBrowser(false);
    setShowGoogleDriveBrowser(false);
  };

  const handleRemoveFile = (fileId) => {
    setSelectedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const handlePublish = async () => {
    if (selectedFiles.length === 0) {
      setAppSnackbar({
        isVisible: true,
        message: 'Please select at least one file to publish',
        type: 'error',
      });
      return;
    }

    if (!selectedMember) {
      setAppSnackbar({
        isVisible: true,
        message: 'Please select a member to upload for',
        type: 'error',
      });
      return;
    }

    setIsPublishing(true);

    // TODO: Implement the actual publish API call here
    // For now, just simulate a delay
    setTimeout(() => {
      setAppSnackbar({
        isVisible: true,
        message: 'Files published successfully!',
        type: 'success',
      });
      setIsPublishing(false);
      // Reset form
      setSelectedFiles([]);
      setSelectedMember(null);
    }, 2000);
  };

  const canPublish = selectedFiles.length > 0 && selectedMember && !isPublishing;

  // Handle OAuth callbacks on component mount
  useEffect(() => {
    handleDropboxOAuthCallback((authData) => {
      // Auth successful, connect the service
      handleServiceConnect('dropbox', authData);
    });

    handleGoogleDriveOAuthCallback((authData) => {
      // Auth successful, connect the service
      handleServiceConnect('googledrive', authData);
    });
    // eslint-disable-next-line
  }, []);

  return (
    <div className={styles.cloudUploadContainer}>
      {/* Header - Close button top right */}
      <div className={styles.header}>
        <button
          className={styles.closeButton}
          onClick={() => navigate('/settings/manage-users')}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Left White Box */}
      <div className={styles.contentWrapper}>
        <div className={styles.mainContent}>
          {/* Cloud Service Connection Section */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Connect Cloud Storage</h2>
            <p className={styles.sectionDescription}>
              Connect Dropbox or Google Drive
            </p>
            <CloudServiceConnector
              connectedServices={connectedServices}
              onDisconnect={handleServiceDisconnect}
            />
          </section>

          {/* File Listing Area */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {selectedFiles.length === 0 ? 'Selected Files' : `${selectedFiles.length} File${selectedFiles.length === 1 ? '' : 's'}`}
            </h2>
            <p className={styles.sectionDescription}>
              {selectedFiles.length === 0 ? 'Import files from your cloud' : 'Ready to upload'}
            </p>
            <FileListingArea
              files={selectedFiles}
              connectedServices={connectedServices}
              onImport={handleImportFiles}
              onRemoveFile={handleRemoveFile}
            />
          </section>

          {/* Member Selection & Publish */}
          <div className={styles.publishSection}>
            <div>
              <h2 className={styles.sectionTitle}>Upload For</h2>
              <p className={styles.sectionDescription}>
                Select member
              </p>
            </div>
            <AffiliateMemberDropdown
              selectedMember={selectedMember}
              onSelectMember={setSelectedMember}
              getToken={getToken}
            />
            <button
              className={`${styles.publishButton} ${canPublish ? styles.publishButtonActive : ''}`}
              onClick={handlePublish}
              disabled={!canPublish}
            >
              {isPublishing ? 'Publishing...' : 'Publish'}
            </button>
          </div>
        </div>
      </div>

      {/* Dropbox File Browser Modal */}
      {showDropboxBrowser && (
        <DropboxFileBrowser
          accessToken={connectedServices.dropbox?.accessToken}
          onClose={() => setShowDropboxBrowser(false)}
          onFilesSelected={(files) => handleFilesSelected(files, 'dropbox')}
        />
      )}

      {/* Google Drive File Browser Modal */}
      {showGoogleDriveBrowser && (
        <GoogleDriveFileBrowser
          accessToken={connectedServices.googledrive?.accessToken}
          onClose={() => setShowGoogleDriveBrowser(false)}
          onFilesSelected={(files) => handleFilesSelected(files, 'googledrive')}
        />
      )}
    </div>
  );
};
